import { useEffect, useState } from 'react'
import { X } from '@phosphor-icons/react'
import { supabase } from '../../lib/supabase'

export default function MemberDetailsDrawer({ isOpen, onClose, memberId }) {
  const [member, setMember] = useState(null)
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('personal')
  const [memberEvents, setMemberEvents] = useState([])

  useEffect(() => {
    if (isOpen && memberId) {
      loadMemberDetails()
    }
  }, [isOpen, memberId])

  const loadMemberDetails = async () => {
    try {
      setLoading(true)

      // Get member basic info
      const { data: memberData } = await supabase
        .from('users')
        .select('*')
        .eq('id', memberId)
        .single()

      // Get member's groups
      const { data: groupMemberships } = await supabase
        .from('group_members')
        .select('group_id, groups(id, name, depth)')
        .eq('user_id', memberId)

      // Get attendance stats
      const today = new Date().toISOString().split('T')[0]
      const { count: totalEvents } = await supabase
        .from('events')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', memberData.org_id)
        .lte('start_date', today)

      const { count: attendedEvents } = await supabase
        .from('attendance_records')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', memberId)

      const attendanceRate = totalEvents > 0 
        ? Math.round((attendedEvents / totalEvents) * 100) 
        : 0

      // Get past events with member's attendance status
      const { data: pastEvents } = await supabase
        .from('events')
        .select('*')
        .eq('org_id', memberData.org_id)
        .lte('start_date', today)
        .order('start_date', { ascending: false })
        .limit(20)

      // Get member's attendance records
      const { data: attendanceRecords } = await supabase
        .from('attendance_records')
        .select('event_id')
        .eq('user_id', memberId)

      // Get member's confirmations
      const { data: confirmations } = await supabase
        .from('event_confirmations')
        .select('event_id, status')
        .eq('user_id', memberId)

      // Process events with status
      const eventsWithStatus = pastEvents.map(event => {
        const attended = attendanceRecords?.find(a => a.event_id === event.id)
        const confirmation = confirmations?.find(c => c.event_id === event.id)

        let status = 'missed'
        if (attended) {
          status = 'attended'
        } else if (confirmation?.status === 'declined') {
          status = 'declined'
        }

        return {
          ...event,
          status
        }
      })

      setMemberEvents(eventsWithStatus)

      // Organize groups by depth
      const team = groupMemberships?.find(gm => gm.groups?.depth === 1)
      const group = groupMemberships?.find(gm => gm.groups?.depth === 2)
      const subgroup = groupMemberships?.find(gm => gm.groups?.depth === 3)

      setMember({
        ...memberData,
        team: team?.groups?.name,
        group: group?.groups?.name,
        subgroup: subgroup?.groups?.name,
        attendanceRate,
        totalEvents: totalEvents || 0,
        attendedEvents: attendedEvents || 0
      })

    } catch (error) {
      console.error('Error loading member details:', error)
    } finally {
      setLoading(false)
    }
  }

  const getRoleLabel = (leadership_level, role, hasTeam) => {
    if (role === 'org_admin') return 'Organization Admin'
    if (leadership_level === 'team') return 'Team lead'
    if (leadership_level === 'group') return 'Sub team head'
    if (leadership_level === 'subgroup') return 'Department H.O.D'
    // If no team assigned, they're a new member
    if (!hasTeam) return 'New member'
    return 'Member'
  }

  const getRoleBadgeColor = (leadership_level, role, hasTeam) => {
    if (role === 'org_admin' || leadership_level === 'team') return 'bg-[#eaf5ed] text-[#2b9646] border border-[#bfe0c8]'
    if (leadership_level === 'group') return 'bg-[#eaf0ff] text-[#2248b2] border border-[#adc2ff]'
    if (leadership_level === 'subgroup') return 'bg-[#f0eeff] text-[#493caf] border border-[#9589fc]'
    // New member badge - gray
    if (!hasTeam) return 'bg-[#f8fafc] text-[#64748b] border border-[#e2e8f0]'
    return 'bg-[#f8fafc] text-[#64748b] border border-[#e2e8f0]'
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric'
    })
  }

  const formatEventDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getStatusBadge = (status) => {
    if (status === 'attended') {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
          Attended
        </span>
      )
    }
    if (status === 'missed') {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
          Missed
        </span>
      )
    }
    if (status === 'declined') {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
          Declined
        </span>
      )
    }
    return null
  }

  if (!isOpen) return null

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl z-50 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">Member details</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={24} className="text-gray-500" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-3">
            <button
              onClick={() => setActiveTab('personal')}
              className={`px-4 py-2 rounded-full text-sm font-regular transition-colors border ${
                activeTab === 'personal'
                  ? 'bg-[#eaf0ff] border-[#2228b6] text-[#2228b6]'
                  : 'bg-[#f8fafc] border-[#e2e8f0] text-[#64748b] hover:border-gray-400'
              }`}
            >
              Personal info
            </button>
            <button
              onClick={() => setActiveTab('organisation')}
              className={`px-4 py-2 rounded-full text-sm font-regular transition-colors border ${
                activeTab === 'organisation'
             ? 'bg-[#eaf0ff] border-[#2228b6] text-[#2228b6]'
                  : 'bg-[#f8fafc] border-[#e2e8f0] text-[#64748b] hover:border-gray-400'
              }`}
            >
              Organisation info
            </button>
            <button
              onClick={() => setActiveTab('attendance')}
              className={`px-4 py-2 rounded-full text-sm font-regular transition-colors border ${
                activeTab === 'attendance'
                 ? 'bg-[#eaf0ff] border-[#2228b6] text-[#2228b6]'
                  : 'bg-[#f8fafc] border-[#e2e8f0] text-[#64748b] hover:border-gray-400'
              }`}
            >
              Attendance info
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : member ? (
          <div className="px-6 py-6">
            {/* Personal Info Tab */}
            {activeTab === 'personal' && (
              <>
                {/* Personal Information Section */}
                <div className="mb-8">
                  <h3 className="text-lg font-medium text-[#64748b] mb-6">Personal information</h3>
                  
                  {/* Profile with name */}
                  <div className="flex items-center gap-4 mb-8">
                    {member.profile_photo_url || member.avatar_url ? (
                      <img
                        src={member.profile_photo_url || member.avatar_url}
                        alt={member.name}
                        className="w-20 h-20 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-bold text-2xl">
                          {member.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="text-2xl font-medium text-[#1e293b]">{member.name}</p>
                      <p className="text-sm text-[#64748b]">Full name</p>
                    </div>
                  </div>
                </div>

                {/* Other Information Section */}
                <div>
                  <h3 className="text-lg font-regular text-[#64748b] mb-6">Other information</h3>
                  
                  <div className="space-y-5">
                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <span className="text-gray-600 font-regular">Email address</span>
                      <span className="text-[#1e293b] font-medium">{member.email}</span>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <span className="text-gray-600 font-regular">Phone number</span>
                      <span className="text-[#1e293b] font-medium">{member.phone || '-'}</span>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <span className="text-gray-600 font-regular">Address</span>
                      <span className="text-[#1e293b] font-medium">{member.address || '-'}</span>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <span className="text-gray-600 font-regular">City</span>
                      <span className="text-[#1e293b] font-medium">{member.city || '-'}</span>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <span className="text-gray-600 font-regular">State</span>
                      <span className="text-[#1e293b] font-medium">{member.state || '-'}</span>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <span className="text-gray-600 font-regular">Country</span>
                      <span className="text-[#1e293b] font-medium">{member.country || '-'}</span>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <span className="text-gray-600 font-regular">Post code</span>
                      <span className="text-[#1e293b] font-medium">{member.postal_code || '-'}</span>
                    </div>

                    <div className="flex items-center justify-between py-3">
                      <span className="text-gray-600 font-regular">Date of birth</span>
                      <span className="text-[#1e293b] font-medium">{formatDate(member.date_of_birth)}</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Organisation Info Tab */}
            {activeTab === 'organisation' && (
              <>
                <div className="mb-8">
                  <h3 className="text-lg font-regular text-[#64748b] mb-6">Organisation information</h3>
                  
                  {/* Role Badge */}
                  <div className="inline-block">
                    <span className={`px-4 py-2 rounded-full text-sm font-medium ${getRoleBadgeColor(member.leadership_level, member.role, member.team)}`}>
                      {getRoleLabel(member.leadership_level, member.role, member.team)}
                    </span>
                  </div>
                </div>

                {/* Your team & role Section - All roles show all three fields */}
                <div>
                  <h3 className="text-lg font-regular text-[#64748b] mb-6">Your team & role</h3>
                  
                  <div className="space-y-5">
                    {/* Team - always shown for all roles */}
                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <span className="text-gray-600 font-regular">Team</span>
                      <span className="text-[#1e293b] font-medium">{member.team || '-'}</span>
                    </div>

                    {/* Sub team - always shown for all roles */}
                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <span className="text-gray-600 font-regular">Sub team</span>
                      <span className="text-[#1e293b] font-medium">{member.group || '-'}</span>
                    </div>

                    {/* Department - always shown for all roles */}
                    <div className="flex items-center justify-between py-3 border-b border-gray-100">
                      <span className="text-gray-600 font-regular">Department</span>
                      <span className="text-[#1e293b] font-medium">{member.subgroup || '-'}</span>
                    </div>

                    {/* Date joined - always shown */}
                    <div className="flex items-center justify-between py-3">
                      <span className="text-gray-600 font-regular">Date joined</span>
                      <span className="text-[#1e293b] font-medium">{formatDate(member.created_at)}</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Attendance Info Tab */}
            {activeTab === 'attendance' && (
              <>
                <div className="mb-8">
                  <h3 className="text-lg font-regular text-[#64748b] mb-3">Attendance information</h3>
                  
                  {/* Stats Cards */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-[#f1f5f9] rounded-lg p-4">
                      <p className="text-sm text-[#475569] mb-1">Attendance rate</p>
                      <p className="text-2xl font-bold text-[#475569]">{member.attendanceRate}%</p>
                    </div>
                    <div className="bg-[#f1f5f9] rounded-lg p-4">
                      <p className="text-sm text-[#475569] mb-1">Events attended</p>
                      <p className="text-2xl font-bold text-[#475569]">{member.attendedEvents}</p>
                    </div>
                    <div className="bg-[#f1f5f9] rounded-lg p-4">
                      <p className="text-sm text-[#475569] mb-1">Events missed</p>
                      <p className="text-2xl font-bold text-[#475569]">{member.totalEvents - member.attendedEvents}</p>
                    </div>
                  </div>
                </div>

                {/* Event List Section */}
                <div>
                  <h3 className="text-lg font-regular text-[#64748b] mb-6">Events</h3>
                  
                  {memberEvents.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No events found</p>
                  ) : (
                    <div className="space-y-3">
                      {memberEvents.map((event) => (
                        <div key={event.id} className="flex items-center gap-3 py-2">
                          {/* Event Image/Thumbnail */}
                          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex-shrink-0 overflow-hidden">
                            {event.banner_url ? (
                              <img 
                                src={event.banner_url} 
                                alt={event.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600"></div>
                            )}
                          </div>

                          {/* Event Details */}
                          <div className="flex-1 min-w-0">
                            <p className="text-base mb-1 font-medium text-[#1e293b] truncate">{event.title}</p>
                            <p className="text-sm text-[#475569]">{formatEventDate(event.start_date)}</p>
                          </div>

                          {/* Status Badge */}
                          {getStatusBadge(event.status)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            Member not found
          </div>
        )}
      </div>
    </>
  )
}